import React, { useEffect, useState } from "react";
import { EventDetail as EventDetailType, AttendanceStatus, TeamAttendanceMap } from "../types";
import {
  fetchEventDetail,
  generateEventAI,
  getCalendarUrl,
  downloadCheatsheetPdf,
  fetchAttendance,
  postAttendance,
  fetchTeamAttendance,
} from "../api";
import { ScoreBadge } from "./ScoreBadge";
import axios from "axios";

interface EventDetailProps {
  eventId: number;
  onCreditsChange?: (remaining: number) => void;
  obvodCode?: string;
}

/* ——— Copy button — glass style ——— */
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      style={{
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: "0.6875rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase" as const,
        padding: "0.3rem 0.75rem",
        borderRadius: "6px",
        border: copied
          ? "1px solid rgba(74, 222, 128, 0.4)"
          : "1px solid rgba(255,255,255,0.12)",
        background: copied
          ? "rgba(74, 222, 128, 0.08)"
          : "rgba(255,255,255,0.05)",
        color: copied ? "#4ade80" : "#a1a1a1",
        cursor: "pointer",
        transition: "all 0.2s ease",
        backdropFilter: "blur(4px)",
      }}
    >
      {copied ? "Zkopírováno" : "Kopírovat"}
    </button>
  );
};

/* ——— Post type label helper ——— */
function postLabel(type: string): string {
  const platform = type.includes("fb")
    ? "Facebook"
    : type.includes("ig")
    ? "Instagram"
    : "";
  const timing = type.includes("pre") ? "PŘED akcí" : "PO akci";
  return platform ? `${platform} — ${timing}` : timing;
}

/* ——— Pill badge ——— */
const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "0.2rem 0.625rem",
      borderRadius: "6px",
      border: "1px solid rgba(255,255,255,0.1)",
      background: "rgba(255,255,255,0.04)",
      fontFamily: "'Inter', sans-serif",
      fontWeight: 300,
      fontSize: "0.6875rem",
      color: "#a1a1a1",
      letterSpacing: "0.01em",
    }}
  >
    {children}
  </span>
);

/* ——— Section card ——— */
const SectionCard: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div
    style={{
      borderRadius: "14px",
      padding: "1.25rem",
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.08)",
      ...style,
    }}
  >
    {children}
  </div>
);

/* ——— Section eyebrow ——— */
const SectionEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p
    className="eyebrow-label"
    style={{ marginBottom: "0.625rem" }}
  >
    {children}
  </p>
);

/* ——— Attendance selector ——— */
interface AttendanceSelectorProps {
  eventId: number;
  obvodCode?: string;
}

const BUTTON_OPTIONS: { value: AttendanceStatus; label: string }[] = [
  { value: "going", label: "Já jdu" },
  { value: "not_going", label: "Nepůjdu" },
  { value: "undecided", label: "Nerozhodnut" },
];

const attendanceBorder = (status: AttendanceStatus, active: boolean): string => {
  if (!active) return "1px solid rgba(255,255,255,0.12)";
  if (status === "going") return "1px solid rgba(74, 222, 128, 0.4)";
  if (status === "not_going") return "1px solid rgba(248, 113, 113, 0.4)";
  return "1px solid rgba(255,255,255,0.3)";
};

const attendanceBg = (status: AttendanceStatus, active: boolean): string => {
  if (!active) return "rgba(255,255,255,0.05)";
  if (status === "going") return "rgba(74, 222, 128, 0.08)";
  if (status === "not_going") return "rgba(248, 113, 113, 0.08)";
  return "rgba(255,255,255,0.08)";
};

const attendanceColor = (status: AttendanceStatus, active: boolean): string => {
  if (!active) return "#a1a1a1";
  if (status === "going") return "#4ade80";
  if (status === "not_going") return "#f87171";
  return "#fafafa";
};

const AttendanceSelector: React.FC<AttendanceSelectorProps> = ({ eventId, obvodCode }) => {
  const [status, setStatus] = useState<AttendanceStatus>("undecided");
  const [teamMap, setTeamMap] = useState<TeamAttendanceMap>({});

  useEffect(() => {
    fetchAttendance(eventId)
      .then(setStatus)
      .catch(() => setStatus("undecided"));

    if (obvodCode) {
      fetchTeamAttendance(obvodCode)
        .then(setTeamMap)
        .catch(() => setTeamMap({}));
    }
  }, [eventId, obvodCode]);

  const handleSelect = async (next: AttendanceStatus) => {
    const prev = status;
    setStatus(next);
    try {
      await postAttendance(eventId, next);
    } catch {
      console.warn("Attendance update failed, reverting");
      setStatus(prev);
    }
  };

  const team = teamMap[String(eventId)];
  const hasTeamData = team && (team.going > 0 || team.not_going > 0 || team.undecided > 0);

  const teamSummary = hasTeamData
    ? `${team.going} jdou · ${team.not_going} nepůjde · ${team.undecided} nerozhodnutí`
    : null;

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
        {BUTTON_OPTIONS.map(({ value, label }) => {
          const isActive = status === value;
          return (
            <button
              key={value}
              onClick={() => handleSelect(value)}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                fontSize: "0.6875rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                padding: "0.3rem 0.75rem",
                borderRadius: "6px",
                border: attendanceBorder(value, isActive),
                background: attendanceBg(value, isActive),
                color: attendanceColor(value, isActive),
                cursor: "pointer",
                transition: "all 0.2s ease",
                backdropFilter: "blur(4px)",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      {teamSummary && (
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300,
            fontSize: "0.6875rem",
            color: "#666666",
            margin: "0.375rem 0 0",
            letterSpacing: "0.01em",
          }}
        >
          {teamSummary}
        </p>
      )}
    </div>
  );
};

export const EventDetailPanel: React.FC<EventDetailProps> = ({ eventId, onCreditsChange, obvodCode }) => {
  const [event, setEvent] = useState<EventDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchEventDetail(eventId)
      .then((data) => {
        setEvent(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Nepodařilo se načíst akci");
        setLoading(false);
      });
  }, [eventId]);

  const handleGenerateAI = async () => {
    setAiLoading(true);
    try {
      const data = await generateEventAI(eventId);
      setEvent(data);
    } catch (e: unknown) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      const detail = axios.isAxiosError(e) ? e.response?.data?.detail : undefined;
      alert(
        status === 402
          ? "Nemáte dostatek kreditů."
          : status === 503
          ? (typeof detail === "string" ? detail : "AI je dočasně přetížená. Zkuste to za chvíli.")
          : "Generování selhalo. Zkuste to znovu."
      );
    } finally {
      setAiLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadCheatsheetPdf(eventId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `priprava-${eventId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      const detail = axios.isAxiosError(e) ? e.response?.data?.detail : undefined;
      alert(
        status === 402
          ? "Nemáte dostatek kreditů."
          : status === 503
          ? (detail || "AI je dočasně přetížená. Zkuste to za chvíli.")
          : "Generování PDF selhalo."
      );
    } finally {
      setPdfLoading(false);
    }
  };

  /* ——— Loading state ——— */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "5rem 0",
        }}
      >
        <div className="dwx-spinner" />
      </div>
    );
  }

  /* ——— Error state ——— */
  if (error || !event) {
    return (
      <div
        style={{
          borderRadius: "12px",
          padding: "1.25rem",
          background: "rgba(248, 113, 113, 0.08)",
          border: "1px solid rgba(248, 113, 113, 0.25)",
          fontFamily: "'Inter', sans-serif",
          fontWeight: 300,
          fontSize: "0.875rem",
          color: "#f87171",
        }}
      >
        {error ?? "Akce nenalezena"}
      </div>
    );
  }

  const formatDate = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("cs-CZ", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const hasAI = !!event.brief || event.social_posts.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {/* ——— Header card ——— */}
      <SectionCard>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(1.25rem, 3vw, 1.75rem)",
                color: "#fafafa",
                letterSpacing: "-0.03em",
                margin: "0 0 0.5rem",
                lineHeight: 1.2,
              }}
            >
              {event.title}
            </h2>
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 300,
                fontSize: "0.8125rem",
                color: "#a1a1a1",
                margin: "0 0 0.25rem",
              }}
            >
              {formatDate(event.date_start)}
            </p>
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 300,
                fontSize: "0.8125rem",
                color: "#a1a1a1",
                margin: "0 0 0.375rem",
              }}
            >
              {event.city}
              {event.street ? `, ${event.street}` : ""}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", marginTop: "0.5rem" }}>
              {event.organizer && <Pill>Pořadatel: {event.organizer}</Pill>}
              {event.contact && <Pill>Kontakt: {event.contact}</Pill>}
            </div>
            <AttendanceSelector eventId={eventId} obvodCode={obvodCode} />
          </div>
          <div style={{ flexShrink: 0 }}>
            <ScoreBadge score={event.priority_score} size="md" />
          </div>
        </div>
      </SectionCard>

      {/* ——— Action buttons ——— */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {!hasAI ? (
          <button
            disabled={aiLoading}
            onClick={handleGenerateAI}
            className="btn-primary-hero"
            style={{ width: "100%", justifyContent: "center", opacity: aiLoading ? 0.6 : 1 }}
          >
            {aiLoading ? (
              <>
                <span className="dwx-spinner" style={{ width: "1rem", height: "1rem", borderWidth: "2px" }} />
                Generuji AI obsah...
              </>
            ) : (
              "Generovat brief + posty (1 kredit)"
            )}
          </button>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            <button
              disabled={pdfLoading}
              onClick={handlePdf}
              className="btn-primary-hero"
              style={{ flex: 1, justifyContent: "center", opacity: pdfLoading ? 0.6 : 1 }}
            >
              {pdfLoading ? (
                <>
                  <span className="dwx-spinner" style={{ width: "1rem", height: "1rem", borderWidth: "2px" }} />
                  Generuji PDF...
                </>
              ) : (
                "Cheat sheet (PDF)"
              )}
            </button>
          </div>
        )}
        <a
          href={getCalendarUrl(eventId)}
          className="btn-secondary-hero"
          style={{ textDecoration: "none", justifyContent: "center" }}
        >
          Přidat do kalendáře
        </a>
      </div>

      {/* ——— Historical Context — shown inline if present in brief ——— */}
      {(event.historical_context ?? null) && (
        <SectionCard>
          <SectionEyebrow>Historický kontext</SectionEyebrow>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              fontSize: "0.8125rem",
              color: "#fafafa",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              margin: 0,
            }}
          >
            {event.historical_context}
          </p>
        </SectionCard>
      )}

      {/* ——— Brief ——— */}
      {event.brief && (
        <SectionCard>
          <SectionEyebrow>Přípravný brief</SectionEyebrow>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              fontSize: "0.8125rem",
              color: "#fafafa",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              margin: 0,
            }}
          >
            {event.brief}
          </p>
        </SectionCard>
      )}

      {/* ——— Social Posts ——— */}
      {event.social_posts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {event.social_posts.map((post, i) => (
            <SectionCard key={i}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                <SectionEyebrow>Post {i + 1} — {postLabel(post.type)}</SectionEyebrow>
                <CopyButton text={`${post.text}\n\n${post.hashtags.join(" ")}`} />
              </div>
              {/* Post sub-card */}
              <div
                style={{
                  borderRadius: "10px",
                  padding: "0.875rem",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 300,
                    fontSize: "0.8125rem",
                    color: "#fafafa",
                    lineHeight: 1.65,
                    margin: "0 0 0.5rem",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {post.text}
                </p>
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 300,
                    fontSize: "0.75rem",
                    color: "#60a5fa",
                    margin: 0,
                    letterSpacing: "0.01em",
                  }}
                >
                  {post.hashtags.join(" ")}
                </p>
              </div>
            </SectionCard>
          ))}
        </div>
      )}

      {/* ——— Description ——— */}
      {event.description && (
        <SectionCard>
          <SectionEyebrow>Popis akce</SectionEyebrow>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              fontSize: "0.8125rem",
              color: "#a1a1a1",
              lineHeight: 1.65,
              margin: 0,
            }}
          >
            {event.description}
          </p>
        </SectionCard>
      )}
    </div>
  );
};
